/**
 * DocumentUploadButton — base64 PDF/image upload tied to an operation.
 *
 * Shows a small file picker; on file pick, reads as base64 and posts to
 * /api/documents. Refreshes the parent on success.
 */
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { uploadDocument } from '@/services/workflowApi';
import { useConnectivity } from '@/hooks/useConnectivity';

export const DocumentUploadButton = ({ operationId, defaultType = 'proof_of_payment', getAuthHeaders, onUploaded }) => {
  const { isOffline } = useConnectivity();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Archivo demasiado grande (max 20MB)');
      e.target.value = '';
      return;
    }
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
      const b64 = btoa(binary);
      await uploadDocument(getAuthHeaders, {
        operation_id: operationId,
        document_type: defaultType,
        filename: file.name,
        content_base64: b64,
        content_type: file.type || 'application/octet-stream',
      });
      toast.success('Documento subido');
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setBusy(false);
      if (e.target) e.target.value = '';
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" hidden onChange={onPick} accept="image/*,application/pdf" data-testid="doc-upload-input" />
      <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy || isOffline} title={isOffline ? 'Sin conexión' : undefined} data-testid="doc-upload-btn">
        <Upload className="w-3.5 h-3.5 mr-1" />
        {busy ? 'Subiendo…' : isOffline ? 'Sin conexión' : 'Adjuntar documento'}
      </Button>
    </>
  );
};

export default DocumentUploadButton;
